import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { FileText, Search, Filter, RefreshCw, CheckCircle, XCircle, Clock, Send, Eye, Plus, Trash2 } from 'lucide-react';
import SendTestMessageModal from './SendTestMessageModal';
import { WabaTemplatePreviewModal } from './WabaTemplatePreviewModal';
import CreateWhatsAppTemplateModal from './CreateWhatsAppTemplateModal';
import DeleteTemplateModal from './DeleteTemplateModal';
import LoadingSpinner from '../ui/LoadingSpinner';

interface TemplatesProps {
  wabaAccountId: string;
}

interface Template {
  id: string;
  name: string;
  status: string;
  category: string;
  language: string;
  components: any[];
  createdAt: string;
  updatedAt: string;
  analytics?: {
    totals: {
      sent: number;
      delivered: number;
      read: number;
      clicked: Array<{ type: string; total: number; unique: number }>;
      cost: Array<{ amount: number; currency: string }>;
    };
    daily: Array<{
      date: string;
      sent: number;
      delivered: number;
      read: number;
    }>;
    lastFetchedAt: string;
    dataRange: { start: string; end: string };
  } | null;
}

export default function Templates({ wabaAccountId }: TemplatesProps) {
  const { accessToken } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showTestModal, setShowTestModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, wabaAccountId]);

  useEffect(() => {
    filterTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, searchQuery, statusFilter]);

  const fetchTemplates = async () => {
    if (!accessToken) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/templates?wabaAccountId=${wabaAccountId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setTemplates(data.data.templates || []);
      } else {
        setError(data.message || 'Erro ao carregar templates');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar templates');
    } finally {
      setIsLoading(false);
    }
  };

  const syncTemplates = async () => {
    if (!accessToken) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/templates/sync`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            wabaAccountId,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        // Após sincronizar, recarrega a lista de templates
        await fetchTemplates();
      } else {
        setError(data.message || 'Erro ao sincronizar templates');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao sincronizar templates');
    } finally {
      setIsLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = templates;

    // Filtro por busca
    if (searchQuery) {
      filtered = filtered.filter((template) =>
        template.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filtro por status
    if (statusFilter !== 'all') {
      filtered = filtered.filter((template) => template.status === statusFilter);
    }

    setFilteredTemplates(filtered);
  };

  const getStatusIcon = (status: string) => {
    if (status === 'APPROVED' || status.startsWith('ACTIVE')) {
      return <CheckCircle size={16} className="text-success" />;
    }
    if (status === 'REJECTED' || status === 'DISABLED') {
      return <XCircle size={16} className="text-danger" />;
    }
    return <Clock size={16} className="text-warning" />;
  };

  const getStatusColor = (status: string) => {
    if (status === 'APPROVED' || status.startsWith('ACTIVE')) {
      return 'bg-success/10 text-success';
    }
    if (status === 'REJECTED' || status === 'DISABLED') {
      return 'bg-danger/10 text-danger';
    }
    return 'bg-warning/10 text-warning';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      PENDING: 'Pendente',
      APPROVED: 'Aprovado',
      REJECTED: 'Rejeitado',
      ACTIVE_QUALITY_PENDING: 'Ativo - Qualidade Pendente',
      ACTIVE_HIGH_QUALITY: 'Ativo - Alta Qualidade',
      ACTIVE_MEDIUM_QUALITY: 'Ativo - Média Qualidade',
      ACTIVE_LOW_QUALITY: 'Ativo - Baixa Qualidade',
      PAUSED: 'Pausado',
      DISABLED: 'Desabilitado',
    };
    return labels[status] || status;
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      UTILITY: 'Utilidade',
      MARKETING: 'Marketing',
      AUTHENTICATION: 'Autenticação',
    };
    return labels[category] || category;
  };

  const handlePreviewTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  const handleOpenDeleteModal = (template: Template) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete || !accessToken) return;

    setIsDeleting(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/api/templates/${templateToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        // Recarregar lista de templates
        await fetchTemplates();
        setShowDeleteModal(false);
        setTemplateToDelete(null);
      } else {
        setError(data.message || 'Erro ao deletar template');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao deletar template');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <LoadingSpinner text="Carregando templates..." />
    );
  }

  const approvedCount = templates.filter(
    (t) => t.status === 'APPROVED' || t.status.startsWith('ACTIVE')
  ).length;

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-text-1">Templates WhatsApp</h2>
          <p className="text-text-3 text-sm mt-1">
            Templates aprovados pelo Meta para envio de mensagens • {templates.length} templates • {approvedCount} aprovados
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-success hover:bg-success/90 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-success focus:ring-offset-2 focus:ring-offset-bg-1"
          >
            <Plus size={18} />
            Criar Template
          </button>
          <button
            onClick={() => setShowTestModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-accent-1 hover:bg-accent-2 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-accent-1 focus:ring-offset-2 focus:ring-offset-bg-1"
          >
            <Send size={18} />
            Enviar Teste
          </button>
          <button
            onClick={syncTemplates}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-bg-2 border border-bg-3 text-text-1 rounded-md hover:bg-bg-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
            Sincronizar
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-danger/10 border border-danger rounded-md p-4">
          <p className="text-danger text-sm">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex gap-4">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3" size={18} />
          <input
            type="text"
            placeholder="Buscar templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-bg-2 border border-bg-3 rounded-md text-text-1 placeholder:text-text-3 focus:outline-none focus:ring-2 focus:ring-accent-1 focus:border-transparent"
          />
        </div>

        {/* Status Filter */}
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3" size={18} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pl-10 pr-8 py-2 bg-bg-2 border border-bg-3 rounded-md text-text-1 focus:outline-none focus:ring-2 focus:ring-accent-1 focus:border-transparent appearance-none cursor-pointer"
          >
            <option value="all">Todos os status</option>
            <option value="APPROVED">Aprovados</option>
            <option value="PENDING">Pendentes</option>
            <option value="REJECTED">Rejeitados</option>
          </select>
        </div>
      </div>

      {/* Empty State */}
      {filteredTemplates.length === 0 && !error ? (
        <div className="bg-bg-2 border border-bg-3 rounded-lg shadow-md p-12 text-center">
          <FileText className="mx-auto mb-4 text-text-3" size={48} />
          <h3 className="text-xl font-semibold text-text-1 mb-2">
            {searchQuery || statusFilter !== 'all'
              ? 'Nenhum template encontrado'
              : 'Nenhum template disponível'}
          </h3>
          <p className="text-text-2 mb-6">
            {searchQuery || statusFilter !== 'all'
              ? 'Tente ajustar os filtros de busca'
              : 'Sincronize os templates do Dialog360 para visualizá-los aqui'}
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <button
              onClick={syncTemplates}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent-1 hover:bg-accent-2 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
              Sincronizar Templates
            </button>
          )}
        </div>
      ) : (
        /* Templates Grid */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-bg-2 border border-bg-3 rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="p-5 pb-3">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-text-1 mb-1">
                      {template.name}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-text-3">
                        {getCategoryLabel(template.category)}
                      </span>
                      <span className="text-xs text-text-3">•</span>
                      <span className="text-xs text-text-3">{template.language}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {getStatusIcon(template.status)}
                  </div>
                </div>

                <div className="mb-3">
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(template.status)}`}
                  >
                    {getStatusLabel(template.status)}
                  </span>
                </div>

                <div className="text-xs text-text-3">
                  Atualizado em {new Date(template.updatedAt).toLocaleDateString('pt-BR')}
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="border-t border-bg-3 p-3 flex gap-2">
                <button
                  onClick={() => handlePreviewTemplate(template)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-accent-2 hover:bg-accent-2/90 text-white rounded-lg font-medium text-sm transition-colors"
                >
                  <Eye size={16} />
                  Ver Preview
                </button>
                <button
                  onClick={() => handleOpenDeleteModal(template)}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-danger hover:bg-danger/90 text-white rounded-lg font-medium text-sm transition-colors"
                  title="Deletar template"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Send Test Message Modal */}
      <SendTestMessageModal
        wabaAccountId={wabaAccountId}
        templates={templates}
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
      />

      {/* Template Preview Modal */}
      {selectedTemplate && (
        <WabaTemplatePreviewModal
          isOpen={showPreviewModal}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedTemplate(null);
          }}
          template={selectedTemplate}
        />
      )}

      {/* Create Template Modal */}
      <CreateWhatsAppTemplateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        wabaAccountId={wabaAccountId}
        onSuccess={async () => {
          // Reset filters to ensure new template is visible
          setStatusFilter('all');
          setSearchQuery('');

          // Reload templates while modal is still open (user sees loading)
          setIsLoading(true);
          await fetchTemplates();
          setIsLoading(false);

          // Close modal after successfully reloading
          setShowCreateModal(false);
        }}
      />

      {/* Delete Template Modal */}
      <DeleteTemplateModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTemplateToDelete(null);
        }}
        template={templateToDelete}
        onConfirm={handleDeleteTemplate}
        isDeleting={isDeleting}
      />
    </div>
  );
}
